class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onCheckoutClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onCheckoutClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton) return;

    event.preventDefault();
    if (checkoutButton.hasAttribute('disabled')) return;

    this.goToCheckout(checkoutButton);
  }

  goToCheckout(checkoutButton) {
    checkoutButton.setAttribute('aria-disabled', 'true');
    checkoutButton.classList.add('loading');

    fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => this.ensureDefaultFreeGift(cart))
      .then((cart) => {
        const subscriptionItems = cart.items.filter((item) => item.selling_plan_allocation);

        if (!subscriptionItems.length) {
          window.location.href = '/checkout';
          return;
        }

        const baseUrl = 'https://justprotein.subscriptionflow.com/en/hosted-page/commerceflow?';
        let urlParts = '';
        let chargeParts = '';
        let couponCode = '';

        cart.items.forEach((item, index) => {
          const productId = item.product_id;
          const quantity = item.quantity;
          let planId;
          let chargePlanId;

          if (item.selling_plan_allocation) {
            const sellingPlanId = item.selling_plan_allocation.selling_plan.id;
            planId = `${item.variant_id}_${sellingPlanId}`;
            chargePlanId = planId;
          } else {
            planId = item.variant_id;
            chargePlanId = `pvp_${item.variant_id}`;
          }

          urlParts += `items[${index}][pr]=${productId}&items[${index}][pl]=${planId}&items[${index}][q]=${quantity}&`;

          const unitPrice = (item.final_line_price / quantity) / 100;
          chargeParts += `charge_prices[${chargePlanId}][ZAR]=${unitPrice.toFixed(2)}&`;

          if (item.discounts && item.discounts.length > 0) {
            couponCode = item.discounts[0].title;
          }
        });

        if (couponCode) {
          urlParts += `coupon_code=${encodeURIComponent(couponCode)}&`;
        }

        window.location.href = `${baseUrl}${urlParts}${chargeParts}cart=${window.location.origin}/cart`;
      })
      .catch(() => {
        window.location.href = '/checkout';
      })
      .finally(() => {
        checkoutButton.removeAttribute('aria-disabled');
        checkoutButton.classList.remove('loading');
      });
  }

  isFreeGiftItem(item) {
    const giftVariantIds = [48376709087446, 48376709710038];
    return giftVariantIds.includes(Number(item.variant_id)) && item.properties && item.properties['_Free gift'];
  }

  getRewardSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      return this.isFreeGiftItem(item) ? total : total + Number(item.final_line_price || 0);
    }, 0);
  }

  ensureDefaultFreeGift(cart) {
    const freeGiftGoal = 140000;
    const defaultGiftVariantId = 48376709087446;
    const rewardSubtotal = this.getRewardSubtotal(cart);
    const hasFreeGift = cart.items.some((item) => this.isFreeGiftItem(item));

    if (rewardSubtotal < freeGiftGoal || hasFreeGift) {
      return Promise.resolve(cart);
    }

    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: defaultGiftVariantId,
        quantity: 1,
        properties: { '_Free gift': 'R1400 reward' },
      }),
    })
      .then((response) => response.json())
      .then(() => fetch(`${window.routes.cart_url}.js`))
      .then((response) => response.json());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.showAddedToCartMessage();
    });
  }

  showAddedToCartMessage() {
    const existingPopup = document.querySelector('.jp-cart-added-popup');
    if (existingPopup) existingPopup.remove();

    const popup = document.createElement('div');
    popup.className = 'jp-cart-added-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', 'Item added to cart');
    popup.innerHTML = `
      <button type="button" class="jp-cart-added-popup__close" aria-label="Close" data-jp-cart-added-close>&times;</button>
      <p class="jp-cart-added-popup__title">Item added to cart</p>
      <button type="button" class="jp-cart-added-popup__checkout" data-jp-cart-added-view-cart>View cart</button>
    `;

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      const cartLinkBounds = cartLink.getBoundingClientRect();
      popup.style.setProperty('--jp-cart-added-top', `${Math.max(cartLinkBounds.bottom + 12, 12)}px`);
      popup.style.setProperty('--jp-cart-added-right', `${Math.max(window.innerWidth - cartLinkBounds.right, 16)}px`);
    }

    popup.querySelector('[data-jp-cart-added-close]').addEventListener('click', () => {
      popup.classList.remove('is-visible');
      popup.addEventListener('transitionend', () => popup.remove(), { once: true });
    });

    popup.querySelector('[data-jp-cart-added-view-cart]').addEventListener('click', () => {
      popup.remove();
      this.open(cartLink);
    });

    document.body.appendChild(popup);
    requestAnimationFrame(() => popup.classList.add('is-visible'));

    window.clearTimeout(this.addedToCartToastTimeout);
    this.addedToCartToastTimeout = window.setTimeout(() => {
      popup.classList.remove('is-visible');
      popup.addEventListener('transitionend', () => popup.remove(), { once: true });
    }, 7000);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
